/** Binds one weakness row's slot to the View N targets hand-off (PV10, SE-8337). */
import React, { memo, useCallback } from "react";

import type { WeakestPositionRow } from "@/features/scouting/hooks/useThreeWeakestPositions";
import type { TacticalSlot } from "@/generated/graphql";

import WatchlistThreeWeakestRowView, {
  targetsLabel,
} from "./WatchlistThreeWeakestRowView";

/** Public props for one row. */
export interface WatchlistThreeWeakestRowProps {
  readonly row: WeakestPositionRow;
  readonly onViewTargets: (slot: TacticalSlot) => void;
  /**
   * The surface's Add-player entry (SE-8414, frame 944:16362). When present,
   * an empty-slot ("No cover") row with nothing in the pool reads "No targets
   * yet · Add player" and opens it for that slot (the scenario opens its Add
   * players panel filtered to it, SE-8526); absent (a viewer), that row keeps
   * "View 0 targets", disabled — copy follows the affordance.
   */
  readonly onAddPlayer?: (slot: TacticalSlot) => void;
}

/**
 * Curries the row's slot into the hand-off so the view gets a stable press,
 * or routes the empty-slot row at zero to Add player where one exists. The
 * discriminator is the row's cover (`kind`), never the count alone: a
 * covered, still-weak slot at zero stays "View 0 targets" (AC 3b).
 * @param props - Row and hand-offs.
 * @param props.row - Weakness row.
 * @param props.onViewTargets - Opens one slot's targets.
 * @param props.onAddPlayer - Opens the add-player flow, when the surface has one.
 */
const WatchlistThreeWeakestRowContainer = ({
  row,
  onViewTargets,
  onAddPlayer,
}: WatchlistThreeWeakestRowProps) => {
  const isAddPlayer =
    onAddPlayer !== undefined &&
    row.kind === "no-cover" &&
    row.targetCount === 0;
  const handleViewTargets = useCallback(
    () => onViewTargets(row.slot),
    [onViewTargets, row.slot]
  );
  const handleAddPlayer = useCallback(
    () => onAddPlayer?.(row.slot),
    [onAddPlayer, row.slot]
  );
  return (
    <WatchlistThreeWeakestRowView
      row={row}
      label={targetsLabel(row, isAddPlayer)}
      isDisabled={!isAddPlayer && !row.targetCount}
      onPress={isAddPlayer ? handleAddPlayer : handleViewTargets}
    />
  );
};

WatchlistThreeWeakestRowContainer.displayName =
  "WatchlistThreeWeakestRowContainer";
export default memo(WatchlistThreeWeakestRowContainer);
